import DOMPurify from 'dompurify';

/*
 * SUPPORT-708 (F-034): SVG-only sanitizer for TOTP QR codes.
 *
 * This module is deliberately separate from `@/utils/safeHtml`, which is
 * HTML-only and bans every SVG element. Fortify + BaconQrCode is the sole
 * legitimate SVG sink in the SPA, and only <SafeQrCode> is authorized to
 * feed it into the DOM. Application-level components must never call this
 * function directly and must never render QR SVG through v-html — the
 * source-level guard (`resources/js/__tests__/v-html-allowlist.test.js`)
 * enforces both rules.
 *
 * The QR SVG produced by Fortify uses only <svg><rect/><g><g><path/></g></g></svg>
 * with a fixed attribute set. The sanitizer allowlist is narrowed to that
 * exact shape; a structural post-check rejects anything else.
 *
 * Representative Fortify output sizes measured against realistic
 * otpauth:// URLs are 4.6–10 KB. The size cap is set at 64 KB — ~6.4×
 * headroom over the largest realistic output — as a defence-in-depth guard
 * against pathological or injected payloads. The primary security
 * mechanism is the allowlist + structural post-validation, not the cap.
 */

const MAX_QR_SVG_BYTES = 64 * 1024;

const QR_ALLOWED_TAGS = ['svg', 'rect', 'g', 'path'];

// Attributes required by Fortify + BaconQrCode's SvgImageBackEnd. Anything
// not listed here is stripped.
const QR_ALLOWED_ATTR = [
    'xmlns',
    'version',
    'width',
    'height',
    'viewBox',
    'x',
    'y',
    'fill',
    'fill-rule',
    'transform',
    'd',
];

// Belt-and-braces deny list — DOMPurify would strip most of these via the
// tight ALLOWED_TAGS/ALLOWED_ATTR anyway, but naming them explicitly makes
// the intent auditable and future-proofs the profile against config drift.
const QR_FORBID_TAGS = [
    'script',
    'style',
    'foreignobject',
    'use',
    'image',
    'a',
    'symbol',
    'desc',
    'title',
    'defs',
    'clippath',
    'mask',
    'pattern',
    'lineargradient',
    'radialgradient',
    'stop',
    'animate',
    'animatetransform',
    'animatemotion',
    'set',
    'marker',
    'filter',
    'view',
    'switch',
];

const QR_FORBID_ATTR = [
    'href',
    'xlink:href',
    'onload',
    'onerror',
    'onclick',
    'onmouseover',
    'onmouseout',
    'onfocus',
    'onblur',
    'onchange',
    'onsubmit',
    'onbegin',
    'onend',
    'onrepeat',
    'onactivate',
    'onmousedown',
    'onmouseup',
    'onmousemove',
    'onkeydown',
    'onkeyup',
    'onkeypress',
    'onanimationstart',
    'onanimationend',
    'onanimationiteration',
    'ontransitionend',
    'src',
    'data',
    'style',
    'externalResourcesRequired',
];

const QR_CONFIG = {
    USE_PROFILES: { svg: true, svgFilters: false, html: false, mathMl: false },
    ALLOWED_TAGS: QR_ALLOWED_TAGS,
    ALLOWED_ATTR: QR_ALLOWED_ATTR,
    FORBID_TAGS: QR_FORBID_TAGS,
    FORBID_ATTR: QR_FORBID_ATTR,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: false,
    RETURN_TRUSTED_TYPE: false,
    SANITIZE_DOM: true,
};

const BANNED_INPUT_TAGS = new Set([
    'script',
    'foreignobject',
    'use',
    'image',
    'a',
    'style',
    'symbol',
    'desc',
    'title',
    'defs',
    'clippath',
    'mask',
    'pattern',
    'lineargradient',
    'radialgradient',
    'stop',
    'animate',
    'animatetransform',
    'animatemotion',
    'set',
    'marker',
    'filter',
    'view',
    'switch',
]);

const BANNED_INPUT_ATTRS = new Set(['href', 'xlink:href', 'src']);

/**
 * Structural pre-check on the raw input DOM. Fortify emits only the exact
 * <svg><rect/><g><g><path/></g></g></svg> shape; if the caller supplied
 * anything else — even a payload that would sanitize to a safe subset — we
 * reject the whole thing. This is the "reject" side of the policy and
 * makes the post-DOMPurify parse a redundant belt.
 *
 * @param {string} html
 * @returns {boolean} true if input passes the pre-check
 */
function preValidateInput(html) {
    let doc;
    try {
        doc = new DOMParser().parseFromString(
            `<div>${html}</div>`,
            'text/html',
        );
    } catch {
        return false;
    }
    const container = doc.body.firstElementChild;
    if (!container) {
        return false;
    }
    const walker = doc.createTreeWalker(container, /* SHOW_ELEMENT */ 1);
    // walker.currentNode is the synthetic <div> wrapper — skip past it.
    let node = walker.nextNode();
    while (node) {
        const tag = node.tagName.toLowerCase();
        if (BANNED_INPUT_TAGS.has(tag)) {
            return false;
        }
        for (const attr of node.getAttributeNames()) {
            const lower = attr.toLowerCase();
            if (lower.startsWith('on') || BANNED_INPUT_ATTRS.has(lower)) {
                return false;
            }
        }
        node = walker.nextNode();
    }
    return true;
}

/**
 * Structurally validate the DOMPurify output.
 *
 * Requirements:
 *   - Parses as HTML with exactly one root element.
 *   - Root is an <svg>.
 *   - Contains at least one <path d="…"> (the QR data itself).
 *   - No `<script>`, `<foreignObject>`, `<use>`, `<image>`, `<a>` at any depth
 *     (DOMPurify already strips these; this is a post-check invariant).
 *   - No attribute name starting with `on` (event handlers) anywhere.
 *
 * @param {string} html
 * @returns {SVGSVGElement|null}
 */
function parseAndValidate(html) {
    if (typeof html !== 'string' || html.length === 0) {
        return null;
    }

    let doc;
    try {
        doc = new DOMParser().parseFromString(
            `<div>${html}</div>`,
            'text/html',
        );
    } catch {
        return null;
    }

    const container = doc.body.firstElementChild;
    if (!container || container.children.length !== 1) {
        return null;
    }

    const root = container.firstElementChild;
    if (!root || root.tagName.toLowerCase() !== 'svg') {
        return null;
    }

    // Every descendant tag must be in the allowlist.
    const walker = doc.createTreeWalker(root, /* NodeFilter.SHOW_ELEMENT */ 1);
    let node = walker.currentNode;
    while (node) {
        const tag = node.tagName.toLowerCase();
        if (!QR_ALLOWED_TAGS.includes(tag)) {
            return null;
        }
        for (const attr of node.getAttributeNames()) {
            const lower = attr.toLowerCase();
            if (lower.startsWith('on') || lower === 'href' || lower === 'xlink:href') {
                return null;
            }
        }
        node = walker.nextNode();
    }

    const path = root.querySelector('path[d]');
    if (!path) {
        return null;
    }

    return root;
}

/**
 * Sanitize a QR-code SVG string.
 *
 * Returns the serialized safe SVG on success. On any failure — oversized
 * input, non-SVG root, multiple roots, banned element or attribute
 * appearing after sanitization — returns an empty string. Callers should
 * treat an empty string as "do not render".
 *
 * Nothing about the input (including OTP URLs, secrets, or user identifiers
 * that may appear inside the QR data) is logged.
 *
 * @param {unknown} svg
 * @returns {string}
 */
export function sanitizeQrCodeSvg(svg) {
    if (typeof svg !== 'string') {
        return '';
    }
    if (svg.length === 0 || svg.length > MAX_QR_SVG_BYTES) {
        return '';
    }

    if (!preValidateInput(svg)) {
        return '';
    }

    const cleaned = DOMPurify.sanitize(svg, QR_CONFIG);
    if (typeof cleaned !== 'string' || cleaned.length === 0) {
        return '';
    }

    const root = parseAndValidate(cleaned);
    if (!root) {
        return '';
    }

    return root.outerHTML;
}

export const __qrPolicy = Object.freeze({
    MAX_QR_SVG_BYTES,
    ALLOWED_TAGS: Object.freeze([...QR_ALLOWED_TAGS]),
    ALLOWED_ATTR: Object.freeze([...QR_ALLOWED_ATTR]),
});
